// ─────────────────────────────────────────────────────────────
// Web push registration.
//
// Everything here is best effort and reports its own state rather than
// throwing: push depends on browser support, an HTTPS origin, a permission
// grant, a reachable push service and VAPID keys on the server. Any one of
// those can be missing, and when one is the app simply falls back to the
// in-page popups it already had.
// ─────────────────────────────────────────────────────────────

import * as api from './api';

// A push subscription needs the VAPID public key as a Uint8Array, but it
// travels as base64url. Browsers give us no helper for this.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

/** Whether this browser could do push at all, before asking anyone anything.
 *  Service workers need a secure context, so plain-HTTP deployments are out —
 *  localhost is exempted by the browser, which is why development works. */
export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window &&
  window.isSecureContext;

let registrationPromise = null;

/** Register the worker once per page load, however many callers ask. */
export const getRegistration = async () => {
  if (!pushSupported()) return null;
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register('/sw.js').catch((e) => {
      console.warn('[push] service worker registration failed', e);
      registrationPromise = null;
      return null;
    });
  }
  return registrationPromise;
};

/** Is this browser already subscribed? Answered without prompting anyone. */
export const currentSubscription = async () => {
  const reg = await getRegistration();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

/**
 * Subscribe this browser and register it with the server.
 *
 * Must be called from a user gesture: the permission prompt is ignored — and
 * the origin penalised by Chrome — when requested on page load.
 *
 * Returns one of: 'subscribed' | 'denied' | 'unsupported' | 'disabled' | 'failed'.
 */
export const subscribe = async () => {
  if (!pushSupported()) return 'unsupported';

  let config;
  try {
    config = await api.getPushConfig();
  } catch {
    return 'failed';
  }
  // The server has no VAPID keys, so there is nothing to subscribe to. Said
  // explicitly rather than reported as a failure, because it is a deployment
  // choice rather than something the user did wrong.
  if (!config?.enabled || !config?.public_key) return 'disabled';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const reg = await getRegistration();
  if (!reg) return 'failed';

  try {
    // Reuse the existing subscription if there is one — re-subscribing
    // returns the same endpoint anyway, and the server upserts on it.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        // Required to be true by every browser: a push must always result in
        // a visible notification, which is exactly what sw.js does.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      });
    }
    const json = sub.toJSON();
    await api.pushSubscribe({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: navigator.userAgent,
    });
    return 'subscribed';
  } catch (e) {
    console.warn('[push] subscribe failed', e);
    return 'failed';
  }
};

/** Stop push on this browser, on both sides. */
export const unsubscribe = async () => {
  const sub = await currentSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* the server record still goes */ }
  try { await api.pushUnsubscribe(endpoint); } catch { /* pruned on first failed send */ }
  return true;
};
